<template>
  <div
    v-if="isSmallScreenDrawerOpen"
    :class="styles.drawerOverlay"
    @click="closeSmallScreenDrawer"
  />

  <aside
    id="app-sidenav"
    :class="[
      styles.sideNav,
      !isSmallScreen && !isPinned && !isHoverExpanded && styles.collapsed,
      !isSmallScreen && !isPinned && styles.overlay,
      isSmallScreenDrawerOpen && styles.drawerOpen,
    ]"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <div :class="styles.header">
      <div :class="styles.desktopHeader">
        <ShipBobLogo
          :icon-only="isCollapsedView"
          :width="isCollapsedView ? 28 : 130"
          :height="isCollapsedView ? 28 : 34"
          text-color="#fff"
        />
        <Button
          v-if="!isSmallScreen && !isCollapsedView"
          v-tooltip.bottom="isPinned ? 'Unpin sidebar' : 'Pin sidebar'"
          :aria-label="isPinned ? 'Unpin sidebar' : 'Pin sidebar'"
          severity="secondary"
          variant="text"
          rounded
          size="small"
          :class="styles.pinButton"
          :pt="{ root: { class: styles.pinButtonRoot } }"
          @click="togglePin"
        >
          <template #icon>
            <span class="material-icons-round" data-size="2xs" aria-hidden="true">push_pin</span>
          </template>
        </Button>
      </div>
      <div :class="styles.mobileHeader">
        <ShipBobLogo width="130" height="34" text-color="#fff" />
      </div>
    </div>

    <nav :class="styles.nav">
      <template v-for="item in menuItems" :key="item.label">
        <router-link
          v-if="item.route"
          v-slot="{ isActive, navigate, href }"
          :to="item.route"
          custom
        >
          <a
            :class="[styles.menuItem, isActive && styles.menuItemActive]"
            :href="href"
            @click="
              (event) => {
                navigate(event);
                closeSmallScreenDrawer();
              }
            "
          >
            <span class="material-icons-round" :class="styles.menuIcon" data-size="sm">{{
              item.icon
            }}</span>
            <span v-if="showLabels" :class="styles.menuLabel">
              {{ item.label }}
            </span>
          </a>
        </router-link>
        <a v-else :class="styles.menuItem" href="#" @click.prevent>
          <span class="material-icons-round" :class="styles.menuIcon" data-size="sm">{{
            item.icon
          }}</span>
          <span v-if="showLabels" :class="styles.menuLabel">
            {{ item.label }}
          </span>
        </a>
      </template>
    </nav>

    <div :class="styles.footer">
      <Button
        variant="text"
        :class="styles.footerButton"
        :pt="{ root: { class: styles.menuItem } }"
        @click="cycleTheme"
      >
        <span class="material-icons-round" :class="styles.menuIcon" data-size="sm">{{
          themeIcon
        }}</span>
        <span v-if="showLabels" :class="styles.menuLabel">{{ themeLabel }}</span>
      </Button>

      <Button
        variant="text"
        :class="styles.footerButton"
        :pt="{ root: { class: styles.menuItem } }"
        @click="handleLogout"
      >
        <span class="material-icons-round" :class="styles.menuIcon" data-size="sm">logout</span>
        <span v-if="showLabels" :class="styles.menuLabel">Logout</span>
      </Button>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { onMounted, computed } from "vue";
import Button from "primevue/button";
import { useSideNav } from "./useSideNav";
import { useTheme } from "./useTheme";
import { defaultMenuItems, type MenuItem } from "./menu";
import ShipBobLogo from "./ShipBobLogo.vue";
import styles from "./SideNav.module.css";
// Relative (not @/) import: reachable from the ./layout expose, whose emitted
// d.ts ships to consumers where the producer's @/ alias can't resolve.
import { useAuthStore } from "../../shared/auth/auth.store";

const {
  isSmallScreen,
  isSmallScreenDrawerOpen,
  isHoverExpanded,
  isPinned,
  initSideNav,
  closeSmallScreenDrawer,
  togglePin,
  onMouseEnter,
  onMouseLeave,
} = useSideNav();

const { themeMode, cycleTheme } = useTheme();

const isCollapsedView = computed(
  () => !isSmallScreen.value && !isPinned.value && !isHoverExpanded.value,
);

const showLabels = computed(() =>
  isSmallScreen.value ? isSmallScreenDrawerOpen.value : isPinned.value || isHoverExpanded.value,
);

const themeIcon = computed(() => {
  if (themeMode.value === "dark") return "dark_mode";
  if (themeMode.value === "light") return "light_mode";
  return "contrast";
});

const themeLabel = computed(() => {
  if (themeMode.value === "dark") return "Dark";
  if (themeMode.value === "light") return "Light";
  return "System";
});

const props = defineProps<{
  /** Menu entries; defaults to this host's own menu. */
  menuItems?: MenuItem[];
}>();

const menuItems = computed(() => props.menuItems ?? defaultMenuItems);

const authStore = useAuthStore();

async function handleLogout() {
  try {
    await authStore.logout();
  } catch (error) {
    console.error("[SideNav] Logout failed:", error);
  }
}

onMounted(() => {
  initSideNav();
});
</script>
